import { OFFLINE_HEADERS } from '@jq4race/shared';

import { classifySyncError } from '../../offline/domain/classify-sync-error.js';
import type { OutboxHandler, OutboxHandlerContext } from '../../offline/domain/outbox-handler.js';
import type { OutboxItem, OutboxLastError } from '../../offline/domain/outbox-item.js';

/**
 * Fourth adversarial review, MEDIUM #2 — a cached `uploadedPhotoKeys` entry
 * is only reused if it still has this much life left before its (client-
 * estimated) expiry. Without a margin, a key that expires DURING the
 * upcoming `createTicket()` round trip would be sent as if still good,
 * yielding a `photo_token_invalid` the client could have avoided by simply
 * re-uploading fresh.
 */
const KEY_REUSE_SAFETY_MARGIN_MS = 60_000;

/** `null` when there is no cached entry for `ref`, or it's too close to expiring to trust. */
function reusableCachedKey(item: OutboxItem, ref: string, now: Date): string | null {
  const cached = item.uploadedPhotoKeys?.[ref];
  if (!cached) return null;
  const usableUntilMs = new Date(cached.expiresAt).getTime() - KEY_REUSE_SAFETY_MARGIN_MS;
  return now.getTime() < usableUntilMs ? cached.key : null;
}

/**
 * checkinCreateHandler (design D7, task 6.5) — the `checkin.create`
 * OutboxHandler. Replays one outbox item: uploads its locally-stored photo
 * blobs (compressed at capture time by LocalPhotoSink, task 6.6), then
 * submits the check-in.
 *
 * Not invoked by anything yet — the SyncEngine that calls handlers by `kind`
 * is Slice 7. This ships now (registered ahead of its caller) so Slice 7
 * only has to wire a dispatch table, never write replay logic itself.
 */
export const checkinCreateHandler: OutboxHandler<'checkin.create'> = {
  kind: 'checkin.create',

  async execute(item: OutboxItem, ctx: OutboxHandlerContext) {
    // BLOCKER #1 — load EXACTLY item.photoRefs (never "every blob under this
    // outboxId"), then re-order the results to match photoRefs' OWN order
    // (the angle order fixed at capture) — a blob store's natural row order
    // is never a substitute for that.
    const blobs = await ctx.getBlobs(item.photoRefs);
    const blobsById = new Map(blobs.map((b) => [b.id, b]));

    const photoKeys: string[] = [];
    for (const ref of item.photoRefs) {
      // Fourth adversarial review, MEDIUM #2 — reuse a still-valid key from
      // a PRIOR attempt instead of re-uploading the same bytes again; on a
      // slow link with several photos, re-uploading everything on every
      // attempt could mean the item never finishes within any deadline.
      const cachedKey = reusableCachedKey(item, ref, ctx.now());
      if (cachedKey) {
        photoKeys.push(cachedKey);
        continue;
      }
      const b = blobsById.get(ref);
      if (!b || !b.role || !b.blob) continue;
      const { key, expiresAt } = await ctx.uploadBlob(b.role, b.blob);
      photoKeys.push(key);
      await ctx.persistUploadedPhotoKey(ref, key, expiresAt);
    }

    // Third adversarial review finding H3 — incident photos are local blobs
    // too now, uploaded fresh at SYNC time (same as the 4 mandatory angles),
    // never read from `item.payload` (a stale capture-time server token with
    // a short, 15-min TTL that a late or different-operator sync could
    // easily land after — silently losing damage evidence with no recovery
    // path).
    //
    // Fourth adversarial review, HIGH #1 — incident-photo-upload-urls is
    // gated behind PlanFeatureGuard('damageReports') on the backend (403
    // feature_not_in_plan, whether the org never had the feature or it was
    // downgraded between capture and sync). Without special-casing this, a
    // failure here would propagate and the drainer would classify the
    // WHOLE item as `reject` — the 4 mandatory photos (already uploaded
    // fine) and the ticket itself would NEVER be created, with no SMS, no
    // recovery. Incident-photo failures are therefore handled LOCALLY:
    // - A RETRYABLE error (network/5xx/429/401/photo_token_invalid, per
    //   classifySyncError's own retry|needs-auth buckets) is RE-THROWN —
    //   the whole item still retries, same as before this fix, since
    //   silently dropping evidence on a flaky connection would be worse
    //   than a delayed sync.
    // - A NON-retryable error (`classifySyncError` → `reject`, e.g. 403
    //   feature_not_in_plan, 422) DROPS every incident key collected so
    //   far, stops attempting any further incident uploads for this item,
    //   and records the reason on the returned result
    //   (`incidentPhotosDropped`) — the ticket is still created with the 4
    //   mandatory photos.
    let incidentPhotoKeys: string[] = [];
    let incidentPhotosDropped: OutboxLastError | undefined;
    try {
      const incidentBlobs = await ctx.getBlobs(item.incidentPhotoRefs);
      const incidentBlobsById = new Map(incidentBlobs.map((b) => [b.id, b]));

      for (const ref of item.incidentPhotoRefs) {
        // MEDIUM #2 — the SAME cached-key reuse rule as the mandatory loop.
        const cachedKey = reusableCachedKey(item, ref, ctx.now());
        if (cachedKey) {
          incidentPhotoKeys.push(cachedKey);
          continue;
        }
        const b = incidentBlobsById.get(ref);
        if (!b || !b.blob) continue;
        const { key, expiresAt } = await ctx.uploadIncidentBlob(b.blob);
        incidentPhotoKeys.push(key);
        await ctx.persistUploadedPhotoKey(ref, key, expiresAt);
      }
    } catch (err) {
      const classified = classifySyncError(err);
      if (classified.kind !== 'reject') throw err;
      incidentPhotoKeys = [];
      incidentPhotosDropped = classified.error;
    }

    const headers: Record<string, string> = {
      // task 6.1 — the SAME value on every attempt (fixed at enqueue).
      [OFFLINE_HEADERS.capturedAt]: item.capturedAt,
      // Slice 5's server-observed-anchor reads THIS fresh on every attempt
      // to correct clock skew — unlike capturedAt, never reuse a value from
      // a prior attempt.
      [OFFLINE_HEADERS.clientSentAt]: ctx.now().toISOString(),
      // BLOCKER #2 — the item's OWN captured location, never the operator's
      // CURRENT ambient location (the drainer runs in the background, after
      // the operator may have since switched locations). api-client's
      // request() lets this caller-supplied value win over the
      // localStorage-derived one (see client.ts).
      'X-Location-Id': item.locationId,
    };

    const body: Record<string, unknown> = {
      ...item.payload,
      clientRequestId: item.id,
      // Slice 5 reconciliation — `capturedAt` is now a real, optional field
      // on `CreateTicketRequestSchema` (packages/shared), consumed by the
      // server-observed-anchor/clock-skew correction and the late-suspension
      // bypass. No longer a speculative unrecognized-field send.
      capturedAt: item.capturedAt,
      photoKeys,
      // Overwrites any stale `incidentPhotoKeys` that might still be sitting
      // in `item.payload` from a pre-H3 enqueue — this fresh, sync-time
      // upload is always the authoritative value.
      incidentPhotoKeys,
    };

    const response = await ctx.createTicket(body, headers);

    return {
      ticketId: response.id,
      ticketNumber: response.ticketNumber,
      smsDispatched: response.smsDispatched,
      coveredByMembership: response.coveredByMembership,
      incidentPhotosDropped,
      checkedInAt: response.checkedInAt,
      checkinTimeSource: response.checkinTimeSource,
    };
  },
};
